/** @jsx jsx */
import { Flex, Status, TabButton } from "@eworkit/iris";
import React, { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { RoutedTabs } from "router";
import { jsx } from "theme-ui";

import { UserContext } from "@/components/User";
import { fetchJobRequestListV1, fetchPublicJobRequestList } from "@/data/job-requests";
import { IPublicProfileInfo, PublicProfile } from "@/data/public-profiles";
import { useFetcher } from "@/hooks/http";
import { useOnLanguageChange } from "@/hooks/Translations";
import { router } from "@/views/router";

export interface IClientPageNavigationTabsProps {
  publicProfile: PublicProfile;
  info?: IPublicProfileInfo;
  isPublic?: boolean;
  forceActiveTab?: string;
}

export const ClientPageNavigationTabs: FC<IClientPageNavigationTabsProps> = React.memo(
  ({ publicProfile, info, isPublic, forceActiveTab }) => {
    const { t } = useTranslation("public");
    const { contextCompany } = useContext(UserContext);

    const lang = useOnLanguageChange();

    const baseRoute = isPublic ? "public-client" : "client";
    const pathLang = isPublic ? `/${lang}` : "";

    const isTabActive = (tab: string, active: boolean) => forceActiveTab === tab || (!forceActiveTab && active);

    const jobFetchFunction = isPublic ? fetchPublicJobRequestList : fetchJobRequestListV1;
    const jobsFetcher = useFetcher(
      jobFetchFunction,
      {
        filtersConfig: {
          publicProfileIds: [publicProfile.id],
        },
        size: 0,
        companyId: contextCompany?.id,
      },
      [isPublic, contextCompany, publicProfile],
    );

    const tabs = useMemo(
      () => ({
        details: (active: boolean, index: number) => (
          <TabButton
            key={index}
            active={isTabActive("details", active)}
            as={Link}
            to={`${pathLang}${router.get(`${baseRoute}.details`, { slug: publicProfile.slug }).path}`}>
            {t("CLIENT_PAGE.DETAILS_TITLE")}
          </TabButton>
        ),
        jobs: (active: boolean, index: number) => (
          <TabButton
            key={index}
            active={isTabActive("jobs", active)}
            variant="primary"
            as={Link}
            to={`${pathLang}${router.get(`${baseRoute}.jobs`, { slug: publicProfile.slug }).path}`}
            placeholderRight={
              typeof jobsFetcher.payload?.totalElements === "number" && jobsFetcher.payload?.totalElements ? (
                <Status size="sm">{jobsFetcher.payload.totalElements}</Status>
              ) : undefined
            }>
            {t("CLIENT_PAGE.JOBS_TITLE")}
          </TabButton>
        ),
        // NOTE: this feature is temporarily hidden
        // location: (active: boolean, index: number) => (
        //   <TabButton
        //     key={index}
        //     active={isTabActive("location", active)}
        //     as={Link}
        //     to={router.get(`${baseRoute}.location`, { slug: publicProfile.slug }).path}>
        //     {t("CLIENT_PAGE.LOCATION_TITLE")}
        //   </TabButton>
        // ),
        news: (active: boolean, index: number) =>
          publicProfile.newsCount ? (
            <TabButton
              key={index}
              active={isTabActive("news", active)}
              variant="primary"
              as={Link}
              to={`${pathLang}${router.get(`${baseRoute}.news`, { slug: publicProfile.slug }).path}`}
              placeholderRight={
                typeof publicProfile.newsCount === "number" ? (
                  <Status size="sm">{publicProfile.newsCount}</Status>
                ) : undefined
              }>
              {t("CLIENT_PAGE.NEWS_TITLE")}
            </TabButton>
          ) : (
            <React.Fragment />
          ),
      }),
      [info?.jobRequestCount, t, isTabActive],
    );

    return (
      <Flex flexDirection="row" alignItems="flex-end" sx={{ overflowX: "auto" }}>
        <RoutedTabs routeParam="tab" views={tabs} />
      </Flex>
    );
  },
);
